import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export const resolveMedia = (url) => (!url ? url : (url.startsWith("http") ? url : `${BACKEND_URL}${url}`));

const TOKEN_KEY = "nead_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && getToken()) {
      clearToken();
      if (window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(error);
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong. Please try again.";
}

// ---- Public ----
export const submitContact = (data) => api.post("/contact", data).then((r) => r.data);
export const submitVolunteer = (data) => api.post("/volunteer", data).then((r) => r.data);
export const submitPartner = (data) => api.post("/partner", data).then((r) => r.data);
export const subscribeNewsletter = (email) => api.post("/newsletter", { email }).then((r) => r.data);
export const getPosts = () => api.get("/blog/posts").then((r) => r.data);
export const getPost = (slug) => api.get(`/blog/posts/${slug}`).then((r) => r.data);
export const createDonation = (data) => api.post("/payments/checkout", data).then((r) => r.data);
export const getPaymentStatus = (id) => api.get(`/payments/status/${id}`).then((r) => r.data);

export const getEvents = () => api.get("/events").then((r) => r.data);
export const getEvent = (slug) => api.get(`/events/${slug}`).then((r) => r.data);
export const getEquipment = () => api.get("/equipment").then((r) => r.data);
export const submitHireQuote = (data) => api.post("/hire/quote", data).then((r) => r.data);
export const buyTickets = (data) => api.post("/tickets/checkout", data).then((r) => r.data);

// ---- Auth ----
export const login = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);

// ---- Admin ----
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminSubmissions = () => api.get("/admin/submissions").then((r) => r.data);
export const adminDeleteSubmission = (id) => api.delete(`/admin/submissions/${id}`).then((r) => r.data);
export const adminNewsletter = () => api.get("/admin/newsletter").then((r) => r.data);
export const adminDeleteNewsletter = (id) => api.delete(`/admin/newsletter/${id}`).then((r) => r.data);
export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const adminEvents = () => api.get("/admin/events").then((r) => r.data);
export const adminCreateEvent = (d) => api.post("/admin/events", d).then((r) => r.data);
export const adminUpdateEvent = (id, d) => api.put(`/admin/events/${id}`, d).then((r) => r.data);
export const adminDeleteEvent = (id) => api.delete(`/admin/events/${id}`).then((r) => r.data);

export const adminBlog = () => api.get("/admin/blog").then((r) => r.data);
export const adminCreateBlog = (d) => api.post("/admin/blog", d).then((r) => r.data);
export const adminUpdateBlog = (slug, d) => api.put(`/admin/blog/${slug}`, d).then((r) => r.data);
export const adminDeleteBlog = (slug) => api.delete(`/admin/blog/${slug}`).then((r) => r.data);

export const adminEquipment = () => api.get("/admin/equipment").then((r) => r.data);
export const adminCreateEquipment = (d) => api.post("/admin/equipment", d).then((r) => r.data);
export const adminUpdateEquipment = (id, d) => api.put(`/admin/equipment/${id}`, d).then((r) => r.data);
export const adminDeleteEquipment = (id) => api.delete(`/admin/equipment/${id}`).then((r) => r.data);
export const adminEquipmentDetail = (id) => api.get(`/admin/equipment/${id}/detail`).then((r) => r.data);
export const adminAddEquipmentLog = (id, d) => api.post(`/admin/equipment/${id}/log`, d).then((r) => r.data);
export const adminDeleteEquipmentLog = (id, logId) => api.delete(`/admin/equipment/${id}/log/${logId}`).then((r) => r.data);

export const adminEnquiries = () => api.get("/admin/hire/enquiries").then((r) => r.data);
export const adminApproveEnquiry = (id) => api.post(`/admin/hire/enquiries/${id}/approve`).then((r) => r.data);
export const adminDeclineEnquiry = (id) => api.post(`/admin/hire/enquiries/${id}/decline`).then((r) => r.data);
export const adminDeleteEnquiry = (id) => api.delete(`/admin/hire/enquiries/${id}`).then((r) => r.data);

export const adminBookings = () => api.get("/admin/bookings").then((r) => r.data);
export const adminUpdateBooking = (id, d) => api.put(`/admin/bookings/${id}`, d).then((r) => r.data);
export const adminDeleteBooking = (id) => api.delete(`/admin/bookings/${id}`).then((r) => r.data);

// ---- Site content (inline editing) ----
export const getContent = () => api.get("/content").then((r) => r.data);
export const updateContent = (key, value) => api.put("/admin/content", { key, value }).then((r) => r.data);

// ---- Galleries ----
export const getGallery = (key) => api.get(`/galleries/${key}`).then((r) => r.data);
export const uploadGalleryImage = (key, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/admin/galleries/${key}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteGalleryImage = (key, imageId) => api.delete(`/admin/galleries/${key}/image/${imageId}`).then((r) => r.data);
export const reorderGallery = (key, order) => api.put(`/admin/galleries/${key}/reorder`, { order }).then((r) => r.data);

// ---- Media overrides (swap any image) ----
export const getMedia = () => api.get("/media").then((r) => r.data);
export const uploadMedia = (key, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/admin/media/${key}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const resetMedia = (key) => api.delete(`/admin/media/${key}`).then((r) => r.data);
